<template>
  <div class="home">
    <SEOHead
      title="Rwanda Kigali Selfdrive | Car Rental Kigali Rwanda"
      description="Rent premium cars in Rwanda with Rwanda Kigali Selfdrive. Explore Rwanda with our luxury fleet including Land Cruisers, SUVs, and economy cars. Competitive rates, full insurance, 24/7 support. Call/WhatsApp: +250 788 539 359"
      keywords="car rental rwanda, rwanda kigali selfdrive, self drive car rental rwanda, rent car rwanda, car hire rwanda, luxury car rental rwanda, 4x4 rental rwanda, suv rental rwanda, airport car rental rwanda, gorilla trekking transport, rwanda safari vehicles, self drive kigali, akagera safari car rental, nyungwe forest transport, lake kivu car hire, kigali airport transfer, rwanda road trip car, budget car rental kigali, land cruiser rental rwanda"
      url="https://rwandakigaliselfdrive.com"
    />
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-background" style="background-image: url('/images/cars/suv/nissan-x-trail.jpg')"></div>
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <div class="hero-left">
          <div class="hero-badge">
            <span>🏆 #1 Car Rental in Rwanda</span>
          </div>
          <h1 class="hero-title">
            Discover Rwanda's
            <!-- <span class="hero-accent">Hidden Gems</span> -->
            <br>With Premium Vehicles
          </h1>
          <p class="hero-subtitle">
            Experience Rwanda's beauty with our handpicked fleet of luxury and adventure vehicles.
          </p>
          <div class="hero-features">
          </div>
          <div class="hero-cta-buttons">
            <button @click="scrollToCarsSection" class="hero-btn primary">Explore Fleet</button>
            <router-link to="/destinations" class="hero-btn secondary">Discover Rwanda</router-link>
          </div>
        </div>
        <div class="hero-right">
          <!-- Vehicle Slider -->
          <div class="vehicle-slider-card">
            <div class="slider-header">
              <h3 class="slider-title">Our Premium Fleet</h3>
              <p class="slider-subtitle">Explore Our Vehicles</p>
            </div>
            <div class="vehicle-slider-container">
              <div class="vehicle-slider" :style="{ transform: `translateX(-${currentVehicleIndex * 100}%)` }">
                <div
                  v-for="(vehicle, index) in featuredVehicles"
                  :key="vehicle.id"
                  class="vehicle-slide"
                  :class="{ 'active': Math.floor(currentVehicleIndex) === index }"
                >
                  <div class="vehicle-image-wrapper">
                    <img :src="vehicle.image" :alt="vehicle.name" class="vehicle-image">
              </div>
                  <div class="vehicle-info">
                    <h4 class="vehicle-name">{{ vehicle.name }}</h4>
                    <p class="vehicle-category">{{ vehicle.category }}</p>
              </div>
                </div>
                </div>
              </div>
            <div class="slider-indicators">
              <span
                v-for="(vehicle, index) in featuredVehicles"
                :key="index"
                class="indicator-dot"
                :class="{ 'active': Math.floor(currentVehicleIndex) === index }"
              ></span>
            </div>
          </div>
        </div>
      </div>
      <div class="hero-scroll-indicator">
        <span>Scroll to explore</span>
        <div class="scroll-arrow"></div>
      </div>
    </section>

    <!-- Book Vehicle Card Section -->
    <section class="book-vehicle-section">
      <div class="book-vehicle-container">
        <div class="book-vehicle-card">
          <div class="book-vehicle-header">
            <div class="book-vehicle-badge">
              <i class="fas fa-check-circle"></i>
              <span>BOOK VEHICLE WITH US</span>
            </div>
          </div>
          <div class="book-vehicle-form">
            <form @submit.prevent="handleBooking" class="booking-form-grid">
              <div class="form-field">
                <label for="pickup-location-booking">Pickup Location</label>
                <input
                  v-model="booking.pickupLocation"
                  type="text"
                  id="pickup-location-booking"
                  placeholder="e.g., Kigali Airport"
                  required
                >
              </div>
              <div class="form-field">
                <label for="dropoff-location-booking">Dropoff Location</label>
                <input
                  v-model="booking.dropoffLocation"
                  type="text"
                  id="dropoff-location-booking"
                  placeholder="e.g., Your Hotel"
                  required
                >
              </div>
              <div class="form-field">
                <label for="pickup-date-booking">Pickup Date</label>
                  <input
                    v-model="booking.pickupDate"
                    type="date"
                  id="pickup-date-booking"
                    required
                  >
                </div>
              <div class="form-field">
                <label for="dropoff-date-booking">Dropoff Date</label>
                  <input
                    v-model="booking.dropoffDate"
                    type="date"
                  id="dropoff-date-booking"
                    required
                  >
                </div>
              <div class="form-field full-width">
                <button type="submit" class="book-vehicle-btn">
                  <span>Search Available Vehicles</span>
                <i class="fas fa-arrow-right"></i>
              </button>
              </div>
            </form>
              </div>
            </div>
      </div>
    </section>

    <!-- Features Section - Stepper -->
    <section class="features">
      <div class="container">
        <h2 class="section-title">Why Choose Us</h2>

        <!-- Progress Stepper -->
        <div class="stepper-container">
          <div class="stepper-progress">
            <div
              v-for="(step, index) in whyChooseUsSteps"
              :key="index"
              class="stepper-step"
              :class="{ 'active': currentStep === index, 'completed': currentStep > index }"
            >
              <div class="step-circle">
                <span v-if="currentStep > index" class="step-check">✓</span>
                <span v-else class="step-number">{{ index + 1 }}</span>
          </div>
              <div class="step-label">{{ step.label }}</div>
          </div>
          </div>

          <!-- Step Content -->
          <div class="stepper-content">
            <div class="step-header">
              <h3 class="step-title">{{ whyChooseUsSteps[currentStep].title }}</h3>
          </div>
            <div class="step-description">
              <p>{{ whyChooseUsSteps[currentStep].description }}</p>
            </div>
            <div class="step-benefits">
              <h4 class="benefits-title">What you get:</h4>
              <ul class="benefits-list">
                <li v-for="(benefit, index) in whyChooseUsSteps[currentStep].benefits" :key="index">
                  <i class="fas fa-check-circle"></i>
                  <span>{{ benefit }}</span>
                </li>
              </ul>
            </div>
            <div class="step-navigation">
              <button
                v-if="currentStep > 0"
                @click="previousStep"
                class="step-btn prev-btn"
              >
                <i class="fas fa-arrow-left"></i>
                <span>Previous</span>
              </button>
              <button
                v-if="currentStep < whyChooseUsSteps.length - 1"
                @click="nextStep"
                class="step-btn next-btn"
              >
                <span>Next</span>
                <i class="fas fa-arrow-right"></i>
              </button>
              <button
                v-else
                @click="resetSteps"
                class="step-btn reset-btn"
              >
                <span>Book Vehicle with Us</span>
                <i class="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Popular Cars Section -->
    <section class="popular-cars">
      <div class="container">
        <h2 class="section-title">Premium Car Rental Fleet in Rwanda</h2>
        <p class="section-subtitle">Discover our carefully curated selection of vehicles perfect for exploring Rwanda's diverse landscapes - from city streets to mountain trails</p>
        <VehicleCards :limit="10" @book-vehicle="handleVehicleBooking" />
        <div class="cars-cta">
          <router-link to="/fleet" class="view-all-btn">View Complete Fleet</router-link>
        </div>
      </div>
    </section>

    <!-- Rwanda Tourism Section -->
    <section class="rwanda-tourism">
      <div class="container">
        <div class="tourism-header">
          <h2 class="section-title">Discover Rwanda - Land of a Thousand Hills</h2>
          <p class="section-subtitle">Experience the magic of Rwanda with our premium car rental service. From mountain gorillas to pristine lakes, every journey is an adventure waiting to unfold.</p>
        </div>
        <div class="destinations-grid">
          <router-link v-for="destination in rwandaDestinations" :key="destination.id" :to="getDestinationLink(destination)" class="destination-card">
            <div class="destination-image">
              <img :src="destination.image" :alt="destination.name">
              <div class="destination-overlay">
                <span class="view-details">Explore Destination</span>
              </div>
              <div class="destination-badge">{{ destination.category }}</div>
            </div>
            <div class="destination-info">
              <h3>{{ destination.name }}</h3>
              <p class="destination-description">{{ destination.shortDescription }}</p>
              <div class="destination-meta">
                <span class="meta-item"><i class="fas fa-map-marker-alt"></i> {{ destination.distance }}</span>
                <span class="meta-item"><i class="fas fa-clock"></i> {{ destination.duration }}</span>
                <span class="meta-item"><i class="fas fa-star"></i> {{ destination.rating }}</span>
              </div>
              <div class="destination-tags">
                <span v-for="tag in destination.tags" :key="tag" class="tag">{{ tag }}</span>
              </div>
              <div class="destination-highlights">
                <h4>Highlights:</h4>
                <ul>
                  <li v-for="highlight in destination.highlights" :key="highlight">{{ highlight }}</li>
                </ul>
              </div>
            </div>
          </router-link>
        </div>
        <div class="tourism-cta">
          <router-link to="/destinations" class="explore-all-btn">Explore All Destinations</router-link>
        </div>
      </div>
    </section>

    <!-- Reviews Section -->
    <ReviewsSection />

    <!-- FAQ Section -->
    <section class="faq-section">
      <div class="container">
        <h2 class="section-title">Frequently Asked Questions</h2>
        <p class="section-subtitle">Everything you need to know about car rental in Rwanda</p>
        <div class="faq-grid">
          <div class="faq-item" v-for="(faq, index) in faqs" :key="index">
            <div class="faq-question" @click="toggleFaq(index)">
              <h3>{{ faq.question }}</h3>
              <i class="fas fa-chevron-down" :class="{ 'rotated': faq.isOpen }"></i>
            </div>
            <div class="faq-answer" :class="{ 'open': faq.isOpen }">
              <p>{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta">
      <div class="container">
        <div class="cta-content">
          <h2>Ready to Explore Kigali?</h2>
          <p>Book your car today and start your adventure</p>
          <router-link to="/cars" class="cta-btn">Browse All Cars</router-link>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-section">
            <h3>Rwanda Kigali Selfdrive</h3>
            <p>Your trusted partner for car rentals and tours in Rwanda. Quality service, competitive rates, and unforgettable experiences.</p>
          </div>
          <div class="footer-section">
            <h4>Quick Links</h4>
            <ul>
              <li><router-link to="/">Home</router-link></li>
              <li><router-link to="/cars">Cars</router-link></li>
              <li><router-link to="/customer-photos">Gallery</router-link></li>
              <li><router-link to="/about">About</router-link></li>
              <li><router-link to="/contact">Contact</router-link></li>
            </ul>
          </div>
          <div class="footer-section">
            <h4>Contact Info</h4>
            <ul>
              <li>📍 Kigali, Rwanda</li>
              <li>📞 +250 788 539 359</li>
              <li>✉️ info@rwandakigaliselfdrive.com</li>
            </ul>
          </div>
          <div class="footer-section">
            <h4>Follow Us</h4>
            <div class="social-links">
              <a href="#" class="social-link">Facebook</a>
              <a href="#" class="social-link">Twitter</a>
              <a href="#" class="social-link">Instagram</a>
            </div>
          </div>
        </div>
        <div class="footer-bottom">
          <p>&copy; {{ new Date().getFullYear() }} Rwanda Kigali Selfdrive. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import SEOHead from '../components/SEOHead.vue'
import ReviewsSection from '../components/ReviewsSection.vue'
import VehicleCards from '../components/VehicleCards.vue'

export default {
  name: 'HomeView',
  components: {
    SEOHead,
    ReviewsSection,
    VehicleCards
  },
  data () {
    return {
      currentVehicleIndex: 0,
      vehiclesPerView: 1,
      vehicleSliderInterval: null,
      currentStep: 0,
      whyChooseUsSteps: [
        {
          label: 'Premium Fleet',
          title: 'Premium Fleet',
          description: 'Choose from our carefully selected fleet of well-maintained vehicles. Each vehicle in our collection is regularly serviced and inspected to ensure the highest standards of safety and reliability for your journey through Rwanda.',
          benefits: [
            'Regularly serviced and maintained vehicles',
            'Wide selection from economy to luxury',
            'All vehicles equipped with modern features',
            '24/7 roadside assistance available'
          ]
        },
        {
          label: 'Competitive Rates',
          title: 'Competitive Rates',
          description: 'Competitive rates with no hidden fees and transparent pricing. We believe in fair and honest pricing, so you know exactly what you\'re paying for without any surprises.',
          benefits: [
            'Transparent pricing with no hidden fees',
            'Competitive rates in the market',
            'Flexible payment options available',
            'Special discounts for long-term rentals'
          ]
        },
        {
          label: 'Full Insurance',
          title: 'Full Insurance',
          description: 'Comprehensive coverage included with every rental. Drive with peace of mind knowing that you and your vehicle are fully protected throughout your rental period.',
          benefits: [
            'Comprehensive insurance coverage included',
            'Third-party liability protection',
            'Collision damage waiver available',
            '24/7 emergency support'
          ]
        },
        {
          label: 'Easy Booking',
          title: 'Easy Booking',
          description: 'Simple online booking process with instant confirmation. Book your vehicle in minutes and get instant confirmation, making your travel planning effortless.',
          benefits: [
            'Quick and easy online booking',
            'Instant confirmation',
            'Flexible cancellation policy',
            'Multiple payment methods accepted'
          ]
        }
      ],
      featuredVehicles: [
        {
          id: 1,
          name: 'Land Cruiser Prado TXL',
          category: 'Luxury SUV',
          image: 'images/cars/suv/toyota-rav4.jpg'
        },
        {
          id: 2,
          name: 'Coastal',
          category: 'Luxury Vehicle',
          image: '/images/vehicles/coastel2.jpeg'
        },
        {
          id: 3,
          name: 'LAND CRUISER TXL',
          category: 'Luxury SUV',
          image: '/images/vehicles/txl.jpeg'
        },
        {
          id: 4,
          name: 'RAVA 4',
          category: '2018',
          image: '/images/cars/luxury/mercedes-gle.jpg'
        },
        {
          id: 5,
          name: 'LAND CRUISER TXL',
          category: '2020',
          image: '/images/cars/luxury/audi-q7-2.jpg'
        }
      ],
      booking: {
        pickupLocation: '',
        dropoffLocation: '',
        pickupDate: '',
        dropoffDate: ''
      },
      popularCars: [
        {
          id: 1,
          name: 'Land Cruiser Prado TXL',
          category: 'Luxury SUV',
          image: '/images/cars/suv/toyota-rav4.jpg',
          transmission: 'Automatic',
          fuel: 'Diesel',
          seats: 7,
          luggage: 4,
          description: 'Perfect for family adventures in Rwanda. This versatile SUV combines comfort, safety, and fuel efficiency, making it ideal for exploring Rwanda\'s diverse landscapes.',
          features: ['Air Conditioning', 'GPS Navigation', 'Bluetooth Connectivity', 'All-Wheel Drive', 'Safety Features', 'Spacious Interior']
        },
        {
          id: 2,
          name: 'Coastal',
          category: 'Luxury Bus',
          image: '/images/vehicles/coastel.jpeg',
          transmission: 'Automatic',
          fuel: 'Diesel',
          seats: 28,
          luggage: 8,
          description: 'The Coastal is a premium luxury bus perfect for group travel, corporate events, and tours in Rwanda. Spacious and comfortable for large groups with ample luggage space.',
          features: ['28 Comfortable Seats', 'Air Conditioning', 'Ample Luggage Space', 'Entertainment System', 'Reclining Seats', 'Onboard Restroom']
        },
        {
          id: 3,
          name: 'TOYOTA RAV4',
          category: 'SUV',
          image: '/images/cars/luxury/mercedes-gle.jpg',
          transmission: 'Automatic',
          fuel: 'Petrol',
          seats: 5,
          luggage: 5,
          description: 'Premium Mercedes GLE SUV offering luxury and performance. Perfect for exploring Rwanda with style and comfort.',
          features: ['Luxury Interior', 'MBUX System', 'GPS Navigation', 'Premium Sound System', 'Leather Seats', 'Advanced Safety']
        },
        {
          id: 4,
          name: 'TOYOTA FORTUNER',
          category: 'Luxury SUV',
          image: '/images/vehicles/fortunes.jpeg',
          transmission: 'Automatic',
          fuel: 'Diesel',
          seats: 7,
          luggage: 6,
          description: 'The Toyota Fortuner combines rugged capability with premium comfort, making it perfect for both city driving and off-road adventures in Rwanda.',
          features: ['4x4 Capability', 'Leather Seats', 'Touchscreen Display', 'Dual Zone AC', 'Advanced Safety', 'Roof Rails']
        },
        {
          id: 5,
          name: 'TOYOTA TUNDRA',
          category: 'Pickup Truck',
          image: '/images/vehicles/tundra.jpeg',
          transmission: 'Automatic',
          fuel: 'Petrol',
          seats: 5,
          luggage: 8,
          description: 'The powerful Toyota Tundra offers exceptional towing capacity and off-road performance, ideal for both work and adventure in Rwanda.',
          features: ['V8 Engine', 'Towing Package', 'Off-Road Suspension', 'Spacious Cabin', 'Infotainment System', 'Heated Seats']
        },
        {
          id: 6,
          name: 'LANDCRUISER SAFARI CAR',
          category: 'Safari Vehicle',
          image: '/images/cars/suv/honda-crv.jpg',
          transmission: 'Automatic',
          fuel: 'Petrol',
          seats: 5,
          luggage: 4,
          description: 'Reliable and spacious SUV perfect for family trips and exploring Rwanda. Excellent safety features and comfortable ride quality.',
          features: ['Honda SENSING', 'Spacious Interior', 'GPS Navigation', 'Bluetooth Connectivity', 'Safety Features', 'All-Weather Capable']
        },
        {
          id: 5,
          name: 'RAV4 2021',
          category: 'SUV',
          image: '/images/cars/suv/mazda-cx5.jpg',
          transmission: 'Automatic',
          fuel: 'Petrol',
          seats: 5,
          luggage: 4,
          description: 'Stylish and efficient SUV with premium interior and excellent driving dynamics. Perfect for exploring Rwanda with style.',
          features: ['Premium Interior', 'SKYACTIV Technology', 'GPS Navigation', 'Bose Sound System', 'Safety Features', 'Fuel Efficient']
        },
        {
          id: 7,
          name: 'TOYOTA RAV4 2008',
          category: 'SUV',
          image: '/images/vehicles/rava42008.jpeg',
          transmission: 'Automatic',
          fuel: 'Petrol',
          seats: 5,
          luggage: 4,
          description: 'Reliable and efficient RAV4 from 2008, perfect for city driving and weekend getaways in Rwanda.',
          features: ['Air Conditioning', 'All-Wheel Drive', 'Spacious Interior', 'Roof Rails', 'ABS Brakes', 'Power Windows']
        },
        {
          id: 8,
          name: 'LANDCRUISER SAFARI',
          category: 'Safari Vehicle',
          image: '/images/vehicles/safaricar.jpeg',
          transmission: 'Manual',
          fuel: 'Diesel',
          seats: 7,
          luggage: 6,
          description: 'The ultimate safari vehicle for exploring Rwanda\'s national parks and rugged terrains with comfort and reliability.',
          features: ['Pop-up Roof', '4x4 Capability', 'Raised Suspension', 'Safari Seating', 'Large Windows', 'Cooler Box']
        },
        {
          id: 9,
          name: 'LANDCRUISER TXL 2024',
          category: 'Luxury SUV',
          image: '/images/vehicles/txl2024.jpeg',
          transmission: 'Automatic',
          fuel: 'Diesel',
          seats: 7,
          luggage: 6,
          description: 'The latest 2024 Land Cruiser TXL model with premium features, perfect for both city and off-road adventures in Rwanda.',
          features: ['Leather Seats', 'Sunroof', '360 Camera', 'Premium Sound', 'LED Headlights', 'Advanced Safety']
        },
        {
          id: 10,
          name: 'LANDCRUISER PRADO',
          category: 'Luxury SUV',
          image: '/images/cars/economy/toyota-vitz.jpg',
          transmission: 'Automatic',
          fuel: 'Petrol',
          seats: 5,
          luggage: 3,
          description: 'Compact and fuel-efficient hatchback perfect for city driving in Rwanda. Easy to park and navigate through busy streets.',
          features: ['Fuel Efficient', 'Easy Parking', 'GPS Navigation', 'Air Conditioning', 'Compact Size', 'Affordable']
        },
        {
          id: 11,
          name: 'SAFARI LANDCRUISER 2',
          category: 'Safari Vehicle',
          image: '/images/vehicles/safaricar2.jpeg',
          transmission: 'Manual',
          fuel: 'Diesel',
          seats: 8,
          luggage: 5,
          description: 'Another excellent safari vehicle option for group tours and wildlife viewing in Rwanda\'s national parks.',
          features: ['Pop-up Roof', '4x4 Capability', 'Raised Suspension', 'Safari Seating', 'Large Windows', 'Cooler Box']
        },
        {
          id: 7,
          name: 'KIA Niro',
          category: 'SUV',
          image: '/images/cars/suv/peugeot-3008.jpg',
          transmission: 'Automatic',
          fuel: 'Petrol',
          seats: 5,
          luggage: 2,
          description: 'Fun and agile small car perfect for navigating Rwanda\'s streets. Great for solo travelers or small groups.',
          features: ['Agile Handling', 'Fuel Efficient', 'Easy Parking', 'Air Conditioning', 'Fun to Drive', 'Budget Friendly']
        },
        {
          id: 12,
          name: 'LAND CRUISER V8',
          category: 'Luxury SUV',
          image: '/images/vehicles/v8.jpg',
          transmission: 'Automatic',
          fuel: 'Diesel',
          seats: 7,
          luggage: 6,
          description: 'The powerful Land Cruiser V8 offers unmatched performance and luxury for your Rwandan adventures, both on and off the road.',
          features: ['V8 Engine', 'Premium Leather', '4x4 Capability', 'Climate Control', 'Advanced Safety', 'Sunroof']
        },
        {
          id: 13,
          name: 'LAND CRUISER V8 PREMIUM',
          category: 'Luxury SUV',
          image: '/images/vehicles/v8_3.jpg',
          transmission: 'Automatic',
          fuel: 'Diesel',
          seats: 7,
          luggage: 6,
          description: 'The premium version of the Land Cruiser V8 with additional luxury features for the ultimate driving experience in Rwanda.',
          features: ['V8 Engine', 'Premium Sound System', '4x4 Capability', 'Heated Seats', '360 Camera', 'Panoramic Sunroof']
        }
      ],
      rwandaDestinations: [
        {
          id: 1,
          name: 'Volcanoes National Park',
          category: 'National Park',
          shortDescription: 'Home to the majestic mountain gorillas and golden monkeys in their natural habitat',
          image: '/images/tourism/volcanoes/national-park.jpg',
          heroImage: '/images/tourism/volcanoes/banner-image-1.gif',
          tags: ['Wildlife', 'Gorillas', 'Hiking', 'Adventure'],
          fullDescription: 'Experience the magic of Volcanoes National Park, where you can trek to see the endangered mountain gorillas in their natural habitat. This UNESCO World Heritage site offers unforgettable wildlife encounters and stunning volcanic landscapes. The park is part of the Virunga Massif and is home to about one-third of the world\'s remaining mountain gorillas.',
          highlights: ['Gorilla Trekking', 'Golden Monkey Trekking', 'Volcano Hiking', 'Cultural Village Visits', 'Bird Watching', 'Nature Walks'],
          bestTime: 'June to September, December to February',
          distance: '2-3 hours from Kigali',
          duration: 'Full day to 3 days',
          rating: '5.0'
        },
        {
          id: 2,
          name: 'Nyungwe Forest National Park',
          category: 'National Park',
          shortDescription: 'Ancient rainforest with diverse wildlife and canopy walks',
          image: '/images/tourism/nyungwe/chimpanzee-nyungwe.jpg',
          heroImage: '/images/tourism/nyungwe/nyungwe-landscape.jpg',
          tags: ['Forest', 'Chimpanzees', 'Canopy Walk', 'Nature'],
          fullDescription: 'Explore one of Africa\'s oldest rainforests, home to chimpanzees, colobus monkeys, and over 300 bird species. The canopy walkway offers breathtaking views of the forest canopy. This ancient forest has been around for over 60 million years and is a biodiversity hotspot.',
          highlights: ['Chimpanzee Trekking', 'Canopy Walkway', 'Bird Watching', 'Waterfall Hikes', 'Forest Walks', 'Primate Tracking'],
          bestTime: 'Year-round, best during dry seasons',
          distance: '5-6 hours from Kigali',
          duration: '2-3 days',
          rating: '4.9'
        },
        {
          id: 3,
          name: 'Akagera National Park',
          category: 'National Park',
          shortDescription: 'Savannah park with the Big Five and boat safaris',
          image: '/images/tourism/akagera/lion-tree.jpg',
          heroImage: '/images/tourism/akagera/akagera-landscape.jpg',
          tags: ['Safari', 'Big Five', 'Lake', 'Wildlife'],
          fullDescription: 'Rwanda\'s only savannah park, Akagera offers traditional African safari experiences with lions, elephants, giraffes, and boat safaris on Lake Ihema.',
          highlights: ['Game Drives', 'Boat Safaris', 'Bird Watching', 'Luxury Lodges'],
          bestTime: 'June to September',
          distance: '2-3 hours from Kigali',
          duration: '2-3 days',
          rating: '4.8'
        },
        {
          id: 4,
          name: 'Lake Kivu',
          category: 'Lake Destination',
          shortDescription: 'Beautiful lake with beaches, water sports, and relaxation',
          image: '/images/tourism/lake-kivu/lake-kivu-rwanda-750x450.jpg',
          heroImage: '/images/tourism/lake-kivu/lake-kivu-rwanda-750x450.jpg',
          tags: ['Lake', 'Beach', 'Water Sports', 'Relaxation'],
          fullDescription: 'Rwanda\'s largest lake offers pristine beaches, water sports, and stunning sunsets. Perfect for relaxation after gorilla trekking adventures.',
          highlights: ['Beach Activities', 'Water Sports', 'Sunset Cruises', 'Lakeside Dining'],
          bestTime: 'Year-round',
          distance: '3-4 hours from Kigali',
          duration: '1-3 days',
          rating: '4.7'
        },
        {
          id: 5,
          name: 'Kigali City',
          category: 'Urban Destination',
          shortDescription: 'Modern capital with rich culture, history, and vibrant markets',
          image: '/images/tourism/kigali-city/Kigali-City..jpg',
          heroImage: '/images/tourism/kigali-city/Kigali-City..jpg',
          tags: ['City', 'Culture', 'History', 'Urban'],
          fullDescription: 'Explore Rwanda\'s vibrant capital with its clean streets, rich history, genocide memorial, and bustling markets. Experience the perfect blend of tradition and modernity.',
          highlights: ['Genocide Memorial', 'Local Markets', 'Coffee Tours', 'City Tours'],
          bestTime: 'Year-round',
          distance: 'City center',
          duration: '1-2 days',
          rating: '4.6'
        },
        {
          id: 6,
          name: 'Musanze Caves',
          category: 'Adventure',
          shortDescription: 'Ancient lava caves with fascinating geological formations',
          image: '/images/tourism/wildlife/leopard-hidden.jpg',
          heroImage: '/images/tourism/wildlife/leopard-hidden.jpg',
          tags: ['Caves', 'Geology', 'Adventure', 'Exploration'],
          fullDescription: 'Explore the fascinating Musanze Caves, formed by ancient lava flows. These caves offer a unique underground adventure with stunning geological formations.',
          highlights: ['Cave Exploration', 'Geological Tours', 'Adventure Activities', 'Local Guides'],
          bestTime: 'Year-round',
          distance: '2 hours from Kigali',
          duration: 'Half day',
          rating: '4.5'
        }
      ],
      faqs: [
        {
          question: 'What documents do I need to rent a car in Rwanda?',
          answer: 'You need a valid driver\'s license (international or from your home country), passport, and credit card. The minimum age is 21 years old.',
          isOpen: false
        },
        {
          question: 'Do you offer insurance with your car rentals?',
          answer: 'Yes, all our rentals include comprehensive insurance coverage. Additional insurance options are available for extra peace of mind.',
          isOpen: false
        },
        {
          question: 'Can I pick up and drop off the car at different locations?',
          answer: 'Yes, we offer flexible pickup and drop-off locations including Kigali International Airport and various locations in Kigali city.',
          isOpen: false
        },
        {
          question: 'What happens if the car breaks down during my rental?',
          answer: 'We provide 24/7 roadside assistance. If the car breaks down due to mechanical issues, we\'ll provide a replacement vehicle at no extra cost.',
          isOpen: false
        },
        {
          question: 'Do you offer long-term car rentals?',
          answer: 'Yes, we offer weekly, monthly, and long-term rental options with discounted rates for extended periods.',
          isOpen: false
        },
        {
          question: 'Can I drive the rental car to other countries?',
          answer: 'Yes, with prior approval. We can arrange cross-border permits for travel to neighboring countries like Uganda, Tanzania, and Kenya.',
          isOpen: false
        }
      ]
    }
  },
  methods: {
    handleBooking () {
      // Save booking data to localStorage
      localStorage.setItem('bookingData', JSON.stringify(this.booking))
      console.log('Booking submitted:', this.booking)
      // Redirect to booking page
      this.$router.push({ path: '/book' })
    },

    scrollToCarsSection () {
      const carsSection = document.querySelector('.popular-cars')
      if (carsSection) {
        carsSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })

        // Add a subtle highlight effect
        carsSection.classList.add('highlight')

        setTimeout(() => {
          carsSection.classList.remove('highlight')
        }, 1000)
      }
    },
    handleVehicleBooking (car) {
      console.log('Vehicle booked from VehicleCards:', car.name)
      // Additional handling if needed
    },
    viewCarDetails (car) {
      console.log('Viewing car details:', car.name)
      // Navigate to fleet page with car details
      this.$router.push({ path: '/fleet', query: { vehicle: car.id } })
    },
    getDestinationLink (destination) {
      // Map destination names to their corresponding routes
      const destinationRoutes = {
        'Volcanoes National Park': '/destinations/volcanoes-national-park',
        'Nyungwe Forest National Park': '/destinations/nyungwe-forest',
        'Akagera National Park': '/destinations/akagera-national-park',
        'Lake Kivu': '/destinations/lake-kivu',
        'Kigali City': '/destinations/kigali-city',
        'Musanze Caves': '/destinations/musanze-caves'
      }
      // Return the mapped route if it exists, otherwise return destinations page
      return destinationRoutes[destination.name] || '/destinations'
    },
    startVehicleSlider () {
      this.vehicleSliderInterval = setInterval(() => {
        this.currentVehicleIndex = (this.currentVehicleIndex + 1) % this.featuredVehicles.length
      }, 4000) // Auto-rotate every 4 seconds
    },
    stopVehicleSlider () {
      if (this.vehicleSliderInterval) {
        clearInterval(this.vehicleSliderInterval)
        this.vehicleSliderInterval = null
      }
    },
    toggleFaq (index) {
      this.faqs[index].isOpen = !this.faqs[index].isOpen
    },
    nextStep () {
      if (this.currentStep < this.whyChooseUsSteps.length - 1) {
        this.currentStep++
      }
    },
    previousStep () {
      if (this.currentStep > 0) {
        this.currentStep--
      }
    },
    resetSteps () {
      this.currentStep = 0
      // Scroll to book vehicle section
      this.scrollToBookVehicle()
    },
    scrollToBookVehicle () {
      const bookSection = document.querySelector('.book-vehicle-section')
      if (bookSection) {
        bookSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }
    }
  },
  mounted () {
    // Scroll to top when component mounts
    window.scrollTo(0, 0)
    // Start vehicle slider auto-rotation
    this.startVehicleSlider()
    // Load booking data from localStorage if available
    const savedBooking = localStorage.getItem('bookingData')
    if (savedBooking) {
      try {
        this.booking = { ...this.booking, ...JSON.parse(savedBooking) }
      } catch (e) {
        console.error('Error loading booking data:', e)
      }
    }
  },
  beforeUnmount () {
    // Clean up interval when component is destroyed
    this.stopVehicleSlider()
  }
}
</script>

<style scoped>
/* Reset and Base Styles */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.home {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.6;
  color: #333;
  scroll-behavior: smooth;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Header */
.header {
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.navbar {
  padding: 1rem 0;
}

.nav-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.logo {
  text-decoration: none;
  font-size: 1.5rem;
  font-weight: bold;
}

.logo-text {
  color: #002F6C;
}

.logo-accent {
  color: #FF8C42;
}

.nav-menu {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.nav-link {
  text-decoration: none;
  color: #666;
  font-weight: 500;
  transition: color 0.3s;
}

.nav-link:hover,
.nav-link.active {
  color: #002F6C;
}

.nav-btn {
  background: #FF8C42;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s;
}

.nav-btn:hover {
  background: #e67e22;
}

/* Hero Section */
.hero {
  position: relative;
  height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(0, 47, 108, 0.1) 0%, rgba(0, 47, 108, 0.05) 50%, rgba(0, 47, 108, 0.15) 100%);
}

.hero-content {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  align-items: center;
  height: 100vh;
}

.hero-left {
  color: white;
  padding-left: 0;
}

.hero-badge {
  display: inline-block;
  background: rgba(255, 140, 66, 0.9);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(10px);
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.2;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.hero-accent {
  color: #FF8C42;
}

.hero-subtitle {
  font-size: 1rem;
  margin-bottom: 1.5rem;
  opacity: 0.95;
  line-height: 1.6;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
}

.hero-features {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.hero-feature {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.feature-icon {
  font-size: 1.2rem;
  filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.3));
}

.hero-cta-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.hero-btn {
  display: inline-block;
  padding: 0.875rem 1.75rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.3s ease;
  text-align: center;
}

.hero-btn.primary {
  background: linear-gradient(135deg, #FF6B35, #FF8C42);
  color: white;
  box-shadow: 0 8px 25px rgba(255, 107, 53, 0.3);
}

.hero-btn.secondary {
  background: transparent;
  color: white;
  border: 2px solid white;
  backdrop-filter: blur(10px);
}

.hero-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.2);
}

.hero-scroll-indicator {
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  text-align: center;
  animation: bounce 2s infinite;
}

.hero-scroll-indicator span {
  display: block;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  opacity: 0.8;
}

.scroll-arrow {
  width: 2px;
  height: 20px;
  background: white;
  margin: 0 auto;
  position: relative;
}

.scroll-arrow::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: -3px;
  width: 8px;
  height: 8px;
  border-right: 2px solid white;
  border-bottom: 2px solid white;
  transform: rotate(45deg);
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateX(-50%) translateY(0);
  }
  40% {
    transform: translateX(-50%) translateY(-10px);
  }
  60% {
    transform: translateX(-50%) translateY(-5px);
  }
}

/* Vehicle Slider Card */
.vehicle-slider-card {
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 500px;
  margin-left: auto;
  width: 100%;
}

.booking-header {
  text-align: center;
  margin-bottom: 2rem;
}

.booking-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #002F6C;
  margin-bottom: 0.5rem;
}

.booking-subtitle {
  color: #666;
  font-size: 1rem;
}

.booking-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #002F6C;
}

.form-group input {
  padding: 0.75rem;
  border: 2px solid #F8F9FA;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.3s;
  background: #F8F9FA;
}

.form-group input:focus {
  outline: none;
  border-color: #FF8C42;
  background: white;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.booking-btn {
  background: linear-gradient(135deg, #FF6B35, #FF8C42);
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
}

.booking-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 107, 53, 0.3);
}

.booking-footer {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
}

.trust-indicators {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #666;
  text-align: center;
}

.trust-indicators span {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.slider-header {
  text-align: center;
  margin-bottom: 2rem;
}

.slider-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #002F6C;
  margin-bottom: 0.5rem;
}

.slider-subtitle {
  color: #666;
  font-size: 1rem;
}

.vehicle-slider-container {
  overflow: hidden;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.vehicle-slider {
  display: flex;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

.vehicle-slide {
  min-width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.vehicle-slide.active {
  opacity: 1;
}

.vehicle-image-wrapper {
  width: 100%;
  height: 280px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background: #f5f5f5;
}

.vehicle-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.vehicle-slide.active .vehicle-image {
  transform: scale(1.05);
}

.vehicle-info {
  text-align: center;
  width: 100%;
}

.vehicle-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #002F6C;
  margin-bottom: 0.5rem;
}

.vehicle-category {
  font-size: 1rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.slider-indicators {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(0, 47, 108, 0.3);
  transition: all 0.3s ease;
  cursor: pointer;
}

.indicator-dot.active {
  background: #002F6C;
  width: 24px;
  border-radius: 4px;
}

/* Book Vehicle Section */
.book-vehicle-section {
  padding: 0;
  margin-top: -80px;
  position: relative;
  z-index: 10;
  margin-bottom: 40px;
}

.book-vehicle-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.book-vehicle-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  padding: 2.5rem;
  position: relative;
}

.book-vehicle-header {
  text-align: center;
  margin-bottom: 2rem;
}

.book-vehicle-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #002F6C, #1E3A5F);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.book-vehicle-badge i {
  font-size: 1rem;
}

.booking-form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-field.full-width {
  grid-column: 1 / -1;
}

.form-field label {
  font-weight: 600;
  color: #002F6C;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.form-field input {
  padding: 0.875rem 1rem;
  border: 2px solid #E5E7EB;
  border-radius: 10px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  background: #F8F9FA;
}

.form-field input:focus {
  outline: none;
  border-color: #FF8C42;
  background: white;
  box-shadow: 0 0 0 3px rgba(255, 140, 66, 0.1);
}

.book-vehicle-btn {
  background: linear-gradient(135deg, #FF6B35, #FF8C42);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  box-shadow: 0 8px 25px rgba(255, 107, 53, 0.3);
}

.book-vehicle-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 35px rgba(255, 107, 53, 0.4);
}

.book-vehicle-btn i {
  font-size: 1.1rem;
  transition: transform 0.3s ease;
}

.book-vehicle-btn:hover i {
  transform: translateX(5px);
}

/* Features Section */
.features {
  padding: 80px 0;
  background: #F8F9FA;
}

.section-title {
  text-align: center;
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 3rem;
  color: #002F6C;
}

/* Stepper Styles */
.stepper-container {
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  position: relative;
}

.stepper-progress {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  position: relative;
}

.stepper-progress::before {
  content: '';
  position: absolute;
  top: 25px;
  left: 0;
  right: 0;
  height: 3px;
  background: #E5E7EB;
  z-index: 0;
}

.stepper-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  z-index: 1;
}

.step-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #E5E7EB;
  color: #9CA3AF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.2rem;
  margin-bottom: 0.75rem;
  transition: all 0.3s ease;
  border: 3px solid #E5E7EB;
}

.stepper-step.active .step-circle {
  background: #FF8C42;
  color: white;
  border-color: #FF8C42;
  transform: scale(1.1);
}

.stepper-step.completed .step-circle {
  background: #10B981;
  color: white;
  border-color: #10B981;
}

.step-check {
  font-size: 1.5rem;
  font-weight: bold;
}

.step-number {
  font-size: 1.2rem;
}

.step-label {
  font-size: 0.85rem;
  color: #6B7280;
  text-align: center;
  font-weight: 500;
  max-width: 100px;
}

.stepper-step.active .step-label {
  color: #002F6C;
  font-weight: 600;
}

.stepper-step.completed .step-label {
  color: #10B981;
}

.stepper-content {
  min-height: 400px;
}

.step-header {
  margin-bottom: 2rem;
}

.step-title {
  font-size: 2rem;
  font-weight: 700;
  color: #FF8C42;
  margin: 0;
}

.step-description {
  margin-bottom: 2rem;
}

.step-description p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #4B5563;
}

.step-benefits {
  background: #F8F9FA;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.benefits-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #002F6C;
  margin-bottom: 1rem;
}

.benefits-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.benefits-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  color: #4B5563;
  font-size: 1rem;
}

.benefits-list li:last-child {
  margin-bottom: 0;
}

.benefits-list li i {
  color: #10B981;
  font-size: 1.1rem;
  margin-top: 0.2rem;
  flex-shrink: 0;
}

.step-navigation {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.step-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.75rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.next-btn {
  background: #FF8C42;
  color: white;
}

.next-btn:hover {
  background: #E67E22;
  transform: translateX(5px);
}

.prev-btn {
  background: #E5E7EB;
  color: #4B5563;
}

.prev-btn:hover {
  background: #D1D5DB;
}

.reset-btn {
  background: #002F6C;
  color: white;
}

.reset-btn:hover {
  background: #1E3A5F;
}

/* Rwanda Tourism Section */
.rwanda-tourism {
  padding: 80px 0;
  background: linear-gradient(135deg, #F8F9FA 0%, #e3f2fd 100%);
}

.tourism-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-subtitle {
  font-size: 1.25rem;
  color: #6b7280;
  margin-top: 1rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.destinations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
}

.destination-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #F8F9FA;
  text-decoration: none;
  color: inherit;
  display: block;
}

.destination-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 16px 40px rgba(0,0,0,0.15);
}

.destination-image {
  position: relative;
  height: 250px;
  overflow: hidden;
}

.destination-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.destination-card:hover .destination-image img {
  transform: scale(1.05);
}

.destination-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 47, 108, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.destination-card:hover .destination-overlay {
  opacity: 1;
}

.view-details {
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid white;
  border-radius: 25px;
  transition: all 0.3s ease;
}

.destination-card:hover .view-details {
  background: white;
  color: #002F6C;
}

.destination-info {
  padding: 2rem;
}

.destination-info h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #002F6C;
}

.destination-description {
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.destination-meta {
  display: flex;
  gap: 15px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9rem;
  color: #666;
}

.meta-item i {
  color: #FF6B35;
  font-size: 0.8rem;
}

.destination-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  background: rgba(0, 47, 108, 0.9);
  color: white;
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
}

.destination-highlights {
  margin-top: 1rem;
}

.destination-highlights h4 {
  font-size: 1rem;
  color: #002F6C;
  margin-bottom: 0.5rem;
}

.destination-highlights ul {
  list-style: none;
  padding: 0;
}

.destination-highlights li {
  padding: 3px 0;
  color: #666;
  font-size: 0.9rem;
  position: relative;
  padding-left: 15px;
}

.destination-highlights li::before {
  content: '•';
  color: #FF6B35;
  position: absolute;
  left: 0;
  font-weight: bold;
}

.destination-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag {
  background: #FF8C42;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
}

.tourism-cta {
  text-align: center;
  margin-top: 3rem;
}

.explore-all-btn {
  display: inline-block;
  padding: 15px 35px;
  background: linear-gradient(135deg, #FF6B35, #FF8C42);
  color: white;
  text-decoration: none;
  border-radius: 30px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.explore-all-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(255, 107, 53, 0.3);
}

/* Popular Cars Section */
.popular-cars {
  padding: 80px 0;
  background: linear-gradient(180deg, #F8F9FA 0%, #FFFFFF 100%);
  transition: all 0.3s ease;
}

.popular-cars.highlight {
  transform: scale(1.02);
  box-shadow: 0 10px 30px rgba(255, 107, 53, 0.2);
}

.cars-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.5rem;
}

.car-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #F0F0F0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.car-card:hover {
  transform: translateY(-12px);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  border-color: #FF8C42;
}

.car-image {
  position: relative;
  height: 280px;
  overflow: hidden;
  background: linear-gradient(135deg, #F8F9FA 0%, #E5E7EB 100%);
}

.car-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.car-card:hover .car-image img {
  transform: scale(1.1);
}

.car-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #FF8C42;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.875rem;
}

.car-info {
  padding: 2rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.car-name {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: #002F6C;
  line-height: 1.3;
}

.car-description {
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
  font-size: 0.95rem;
}

.car-specs {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-bottom: 20px;
}

.spec {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #555;
}

.spec i {
  color: #FF6B35;
  width: 16px;
}

.car-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 25px;
}

.feature {
  background: #f8f9fa;
  color: #002F6C;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
}

.car-pricing {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.price-info {
  display: flex;
  flex-direction: column;
}

.price {
  font-size: 1.5rem;
  font-weight: 700;
  color: #002F6C;
}

.price-note {
  font-size: 0.8rem;
  color: #666;
}

.car-actions {
  margin-top: auto;
  padding-top: 1.5rem;
  display: flex;
  gap: 10px;
}

.car-btn {
  flex: 1;
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.car-btn.primary {
  background: linear-gradient(135deg, #FF6B35, #FF8C42);
  color: white;
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);
}

.car-btn.secondary {
  background: transparent;
  border: 2px solid #002F6C;
  color: #002F6C;
}

.car-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 107, 53, 0.4);
}

.car-btn.primary:hover {
  background: linear-gradient(135deg, #E67E22, #FF6B35);
}

.cars-cta {
  text-align: center;
  margin-top: 3rem;
}

.view-all-btn {
  display: inline-block;
  padding: 15px 35px;
  background: linear-gradient(135deg, #002F6C, #1E3A5F);
  color: white;
  text-decoration: none;
  border-radius: 30px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.view-all-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0, 47, 108, 0.3);
}

/* Testimonials Section */
.testimonials {
  padding: 80px 0;
  background: #F8F9FA;
}

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.testimonial-card {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
}

.testimonial-content {
  margin-bottom: 1.5rem;
}

.testimonial-content p {
  font-style: italic;
  color: #6b7280;
  line-height: 1.6;
}

.testimonial-author {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.testimonial-author img {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
}

.author-info h4 {
  color: #002F6C;
  margin-bottom: 0.25rem;
}

.author-info span {
  color: #6b7280;
  font-size: 0.875rem;
}

/* CTA Section */
.cta {
  padding: 80px 0;
  background: #002F6C;
  color: white;
  text-align: center;
}

.cta-content h2 {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.cta-content p {
  font-size: 1.25rem;
  margin-bottom: 2rem;
  opacity: 0.9;
}

.cta-btn {
  display: inline-block;
  background: #FF8C42;
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  transition: background 0.3s;
}

.cta-btn:hover {
  background: #e67e22;
}

/* Footer */
.footer {
  background: #1E3A5F;
  color: white;
  padding: 60px 0 20px;
}

.footer-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
}

.footer-section h3,
.footer-section h4 {
  margin-bottom: 1rem;
  color: #FF8C42;
}

.footer-section p {
  color: #d1d5db;
  line-height: 1.6;
}

.footer-section ul {
  list-style: none;
}

.footer-section ul li {
  margin-bottom: 0.5rem;
}

.footer-section ul li a {
  color: #d1d5db;
  text-decoration: none;
  transition: color 0.3s;
}

.footer-section ul li a:hover {
  color: #FF8C42;
}

.social-links {
  display: flex;
  gap: 1rem;
}

.social-link {
  color: #d1d5db;
  text-decoration: none;
  transition: color 0.3s;
}

.social-link:hover {
  color: #FF8C42;
}

.footer-bottom {
  border-top: 1px solid #374151;
  padding-top: 2rem;
  text-align: center;
  color: #9ca3af;
}

/* FAQ Section */
.faq-section {
  padding: 4rem 0;
  background: #f8f9fa;
}

.faq-section .section-title {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #002F6C;
}

.faq-section .section-subtitle {
  text-align: center;
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.faq-grid {
  max-width: 800px;
  margin: 0 auto;
}

.faq-item {
  background: white;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

.faq-question {
  padding: 1.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.3s;
}

.faq-question:hover {
  background-color: #f8f9fa;
}

.faq-question h3 {
  font-size: 1.1rem;
  color: #002F6C;
  margin: 0;
  font-weight: 600;
}

.faq-question i {
  color: #FF8C42;
  transition: transform 0.3s;
}

.faq-question i.rotated {
  transform: rotate(180deg);
}

.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
}

.faq-answer.open {
  max-height: 200px;
}

.faq-answer p {
  padding: 0 1.5rem 1.5rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

/* Responsive Design */

/* Large Desktop */
@media (min-width: 1440px) {
  .container {
    max-width: 1400px;
    padding: 0 3rem;
  }
  .hero-content {
    max-width: 1600px;
    padding: 0 60px;
  }
  .hero-title {
    font-size: 3rem;
  }
  .section-title {
    font-size: 3rem;
  }
}

/* Desktop */
@media (max-width: 1200px) {
  .container {
    padding: 0 1.5rem;
  }
  .hero-content {
    padding: 0 30px;
    gap: 3rem;
  }
  .hero-title {
    font-size: 2.2rem;
  }
  .cars-grid {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
  }
  .destinations-grid {
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  }
}

/* Tablet */
@media (max-width: 1024px) {
  .hero {
    height: auto;
    min-height: 100vh;
    padding: 2rem 0;
  }
  .hero-content {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
    padding: 2rem 25px;
    height: auto;
    align-items: center;
  }
  .hero-title {
    font-size: 2.2rem;
  }
  .hero-subtitle {
    font-size: 1rem;
  }
  .hero-cta-buttons {
    display: none;
  }
  .vehicle-slider-card {
    max-width: 400px;
    margin: 0 auto;
    padding: 1.5rem;
  }
  .vehicle-image-wrapper {
    height: 200px;
  }
  .stepper-container {
    padding: 2.5rem 2rem;
  }
  .stepper-progress {
    gap: 1.5rem;
  }
  .step-circle {
    width: 45px;
    height: 45px;
  }
  .cars-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
  .destinations-grid {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
  .book-vehicle-card {
    padding: 2rem;
  }
  .booking-form-grid {
    gap: 1.25rem;
  }
}

/* Large Mobile */
@media (max-width: 768px) {
  .hero {
    height: auto;
    min-height: 90vh;
    padding: 1.5rem 0;
  }
  .hero-content {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    text-align: center;
    height: auto;
    padding: 1.5rem 20px;
  }
  .hero-title {
    font-size: 1.8rem;
    line-height: 1.1;
  }
  .hero-subtitle {
    font-size: 0.9rem;
  }
  .hero-badge {
    font-size: 0.75rem;
    padding: 0.35rem 0.7rem;
  }
  .hero-features {
    display: none;
  }
  .hero-cta-buttons {
    display: none;
  }
  .hero-btn {
    width: 100%;
    max-width: 250px;
    padding: 0.7rem 1.3rem;
    font-size: 0.85rem;
  }
  .vehicle-slider-card {
    max-width: 100%;
    margin: 0 auto;
    padding: 1.25rem;
    min-height: 320px;
  }
  .vehicle-image-wrapper {
    height: 160px;
  }
  .slider-title {
    font-size: 1.2rem;
  }
  .vehicle-name {
    font-size: 1rem;
  }
  .section-title {
    font-size: 2rem;
  }
  .section-subtitle {
    font-size: 1.1rem;
  }
  .stepper-container {
    padding: 2rem 1.5rem;
  }
  .stepper-progress {
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
  }
  .step-circle {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
  .step-label {
    font-size: 0.75rem;
    max-width: 80px;
  }
  .step-title {
    font-size: 1.5rem;
  }
  .step-description p {
    font-size: 1rem;
  }
  .step-navigation {
    flex-direction: column;
    gap: 0.75rem;
  }
  .step-btn {
    width: 100%;
    justify-content: center;
    padding: 1rem 1.5rem;
  }
  .cars-grid,
  .destinations-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  .car-card {
    max-width: 500px;
    margin: 0 auto;
  }
  .destination-card {
    max-width: 500px;
    margin: 0 auto;
  }
  .book-vehicle-section {
    margin-top: -60px;
    margin-bottom: 30px;
  }
  .book-vehicle-card {
    padding: 1.5rem;
    border-radius: 15px;
  }
  .booking-form-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  .book-vehicle-badge {
    font-size: 0.8rem;
    padding: 0.6rem 1.2rem;
  }
  .faq-grid {
    padding: 0 1rem;
  }
  .footer-content {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
}

/* Small Mobile */
@media (max-width: 480px) {
  .container {
    padding: 0 1rem;
  }
  .hero {
    height: auto;
    min-height: 80vh;
    padding: 1rem 0;
  }
  .hero-content {
    padding: 1rem 15px;
    gap: 1.25rem;
  }
  .hero-title {
    font-size: 1.6rem;
  }
  .hero-subtitle {
    font-size: 0.85rem;
  }
  .hero-badge {
    font-size: 0.7rem;
    padding: 0.3rem 0.6rem;
  }
  .hero-btn {
    max-width: 100%;
    padding: 0.65rem 1.1rem;
    font-size: 0.8rem;
  }
  .vehicle-slider-card {
    padding: 1rem;
    min-height: 280px;
  }
  .vehicle-image-wrapper {
    height: 140px;
  }
  .slider-title {
    font-size: 1.1rem;
  }
  .vehicle-name {
    font-size: 0.95rem;
  }
  .section-title {
    font-size: 1.75rem;
  }
  .section-subtitle {
    font-size: 1rem;
  }
  .stepper-container {
    padding: 1.5rem 1rem;
  }
  .stepper-progress {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }
  .stepper-progress::before {
    display: none;
  }
  .step-circle {
    width: 50px;
    height: 50px;
    font-size: 1.1rem;
  }
  .step-label {
    font-size: 0.8rem;
    max-width: 120px;
    text-align: center;
  }
  .step-title {
    font-size: 1.3rem;
  }
  .step-description p {
    font-size: 0.95rem;
  }
  .benefits-list li {
    font-size: 0.9rem;
  }
  .car-specs {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  .car-features {
    gap: 0.5rem;
  }
  .feature {
    font-size: 0.75rem;
    padding: 0.3rem 0.8rem;
  }
  .destination-meta {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  .book-vehicle-section {
    margin-top: -40px;
    margin-bottom: 20px;
  }
  .book-vehicle-card {
    padding: 1.25rem;
  }
  .book-vehicle-badge {
    font-size: 0.75rem;
    padding: 0.5rem 1rem;
  }
  .form-field input {
    padding: 0.75rem 0.875rem;
    font-size: 0.9rem;
  }
  .book-vehicle-btn {
    padding: 0.875rem 1.5rem;
    font-size: 0.95rem;
  }
  .faq-question h3 {
    font-size: 1rem;
  }
  .faq-answer p {
    font-size: 0.9rem;
  }
  .footer-content {
    grid-template-columns: 1fr;
    text-align: center;
  }
}

/* Extra Small Mobile */
@media (max-width: 360px) {
  .hero {
    height: auto;
    min-height: 75vh;
    padding: 0.75rem 0;
  }
  .hero-content {
    padding: 0.75rem 10px;
    gap: 1rem;
  }
  .hero-title {
    font-size: 1.4rem;
  }
  .hero-subtitle {
    font-size: 0.8rem;
  }
  .hero-btn {
    padding: 0.6rem 1rem;
    font-size: 0.75rem;
  }
  .vehicle-slider-card {
    padding: 0.75rem;
    min-height: 250px;
  }
  .vehicle-image-wrapper {
    height: 120px;
  }
  .slider-title {
    font-size: 1rem;
  }
  .vehicle-name {
    font-size: 0.9rem;
  }
  .section-title {
    font-size: 1.5rem;
  }
  .stepper-container {
    padding: 1rem 0.75rem;
  }
  .step-title {
    font-size: 1.2rem;
  }
  .book-vehicle-card {
    padding: 1rem;
  }
  .form-field input {
    padding: 0.7rem 0.75rem;
  }
  .book-vehicle-btn {
    padding: 0.8rem 1.25rem;
    font-size: 0.9rem;
  }
}

/* Touch Device Optimizations */
@media (hover: none) and (pointer: coarse) {
  .hero-btn,
  .car-btn,
  .step-btn,
  .book-vehicle-btn,
  .cta-btn {
    min-height: 48px;
    min-width: 48px;
  }
  .hamburger {
    padding: 0.5rem;
    min-height: 48px;
    min-width: 48px;
  }
  .close-btn {
    min-height: 48px;
    min-width: 48px;
  }
  .faq-question {
    min-height: 60px;
  }
  .mobile-link,
  .mobile-dropdown-toggle {
    min-height: 48px;
    display: flex;
    align-items: center;
  }
}
</style>
